import isEmpty from 'lodash/isEmpty';
import * as React from 'react';
import ReactDOM from 'react-dom';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
  innerPopup,
  popup,
  topLevelPopup,
} from '../../../data/providers/popupsProvider';

const fadeInAnimation = keyframes`
 from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const Popup = styled.div.attrs({
  'aria-modal': true,
  role: 'dialog',
})`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 100;
  -webkit-animation-duration: 0.5s;
  animation-duration: 0.5s;
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
  animation-name: ${fadeInAnimation};
  animation-duration: 0.2s;
  animation-timing-function: ease-in-out;
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const InnerPopup = styled(Popup as any)`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 102;
`;

const PopupBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
`;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const InnerPopupBackground = styled(PopupBackground as any)`
  width: 100%;
  height: 100%;
`;

/**
 * @description hides sibling elements of a modal
 */
const captureVisibility = (id: string): void => {
  /// hide the main app
  const elMeasurement = document.getElementById('recharts_measurement_span');
  if (elMeasurement) {
    elMeasurement.ariaHidden = 'true';
  }
  const elMount = document.getElementById('mount');
  if (elMount) {
    elMount.ariaHidden = 'true';
  }
  /// show the previous modal, if there is one
  switch (id) {
    case 'top-level-popup':
      const elInner = document.getElementById('inner-popup');
      if (elInner) {
        elInner.ariaHidden = 'true';
      }
      return;
    case 'inner-popup':
      const elPopup = document.getElementById('popup');
      if (elPopup) {
        elPopup.ariaHidden = 'true';
      }

      return;
    default:
      return;
  }
};

/**
 * @description shows sibling elements of a modal
 */
const releaseVisibility = (id: string): void => {
  /// show next logical modal, if there is one
  switch (id) {
    case 'top-level-popup':
      const elInner = document.getElementById('inner-popup');
      if (elInner) {
        elInner.ariaHidden = 'false';
      }
      return;
    case 'inner-popup':
      const elPopup = document.getElementById('popup');
      if (elPopup) {
        elPopup.ariaHidden = 'false';
      }
      return;
    default:
      /// show the main app
      const elMount = document.getElementById('mount');
      if (elMount) {
        elMount.ariaHidden = 'false';
      }
      return;
  }
};

export const Popups = withRouter(
  (props: RouteComponentProps): React.ReactElement => {
    const rootRef = React.useRef();
    const elementId = 'popup';
    const [popupContent, setPopupContent] =
      React.useState<React.ReactElement>();

    React.useEffect((): (() => void) => {
      const listener = popup.subscribe((content: React.ReactElement): void => {
        setPopupContent(content);
        if (content === null) {
          releaseVisibility(elementId);
        } else {
          captureVisibility(elementId);
        }
      });

      return (): void => {
        listener.unsubscribe();
      };
    }, []);

    React.useEffect((): void => {
      if (isEmpty(props.location.hash)) {
        popup.next(null);
      }
    }, [props.location.pathname, props.location.hash]);

    const getRootRef = () => rootRef.current;

    return ReactDOM.createPortal(
      popupContent ? (
        <Popup id={elementId} ref={rootRef}>
          <PopupBackground />

          <div tabIndex={0}>
            {React.cloneElement(popupContent, {
              style: { ...popupContent.props.style, zIndex: 101 },
              getRootRef,
            })}
          </div>
        </Popup>
      ) : null,
      document.body
    );
  }
);

export const InnerPopups = withRouter(
  (props: RouteComponentProps): React.ReactElement => {
    const elementId = 'inner-popup';
    const [popupContent, setPopupContent] =
      React.useState<React.ReactElement>();

    /**
     * @description subscribe to inner popup observable
     */
    React.useEffect((): (() => void) => {
      const listener = innerPopup.subscribe(
        (content: React.ReactElement): void => {
          setPopupContent(content);
          if (content === null) {
            releaseVisibility(elementId);
          } else {
            captureVisibility(elementId);
          }
        }
      );
      return (): void => {
        listener.unsubscribe();
      };
    }, []);

    React.useEffect((): void => {
      const hashSegs = props.location.hash.split('/');

      if (hashSegs.length < 2) {
        innerPopup.next(null);
      }
    }, [props.location.pathname, props.location.hash]);

    return ReactDOM.createPortal(
      popupContent ? (
        <InnerPopup id={elementId}>
          <InnerPopupBackground />

          <div tabIndex={0}>
            {React.cloneElement(popupContent, {
              style: { ...popupContent.props.style, zIndex: 102 },
            })}
          </div>
        </InnerPopup>
      ) : null,
      document.body
    );
  }
);

export const TopLevelPopup = (): React.ReactElement => {
  const elementId = 'top-level-popup';
  const [popupContent, setPopupContent] = React.useState<React.ReactElement>();
  React.useEffect((): (() => void) => {
    const listener = topLevelPopup.subscribe(
      (content: React.ReactElement): void => {
        setPopupContent(content);
        if (content === null) {
          releaseVisibility(elementId);
        } else {
          captureVisibility(elementId);
        }
      }
    );
    return (): void => {
      listener.unsubscribe();
    };
  }, []);

  return ReactDOM.createPortal(
    popupContent ? (
      <InnerPopup id={elementId}>
        <InnerPopupBackground />

        {React.cloneElement(popupContent, {
          style: { ...popupContent.props.style, zIndex: 103 },
        })}
      </InnerPopup>
    ) : null,
    document.body
  );
};
